import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button, ButtonLink } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import type {
  AdminBlogPostFull,
  AdminBlogTradeOption,
  AdminRouteMap,
} from "../types";

type AdminBlogEditPageProps = {
  post: AdminBlogPostFull;
  adminRoutes: AdminRouteMap;
  csrfToken: string;
  tradeOptions: AdminBlogTradeOption[];
};

const STATUS_BADGE: Record<string, string> = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

export const AdminBlogEditPage: React.FC<AdminBlogEditPageProps> = ({
  post,
  adminRoutes,
  csrfToken,
  tradeOptions,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const updateAction = adminRoutes.editAdminBlogPostBase
    .replace("/edit", "")
    .replace(":id", String(post.id));
  const publishAction = `${updateAction}/publish`;
  const unpublishAction = `${updateAction}/unpublish`;
  const destroyAction = updateAction;

  const tradeTagsString = post.tradeTags.join(",");
  const badgeClass = STATUS_BADGE[post.status] ?? STATUS_BADGE.draft;
  const publicHref = `/blog/${post.slug}`;
  const verifyMatches = post.bodyMd.match(/\[VERIFY\][^\n]{0,200}/gi) ?? [];

  const headerActions = (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${badgeClass}`}
      >
        {post.status}
      </span>
      {post.status === "published" ? (
        <form method="post" action={unpublishAction} className="inline">
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <Button type="submit" variant="outline" size="sm">
            Unpublish
          </Button>
        </form>
      ) : (
        <form method="post" action={publishAction} className="inline">
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <Button type="submit" size="sm">
            Publish
          </Button>
        </form>
      )}
      <form
        method="post"
        action={destroyAction}
        className="inline"
        onSubmit={(e) => {
          if (!window.confirm("Delete this post? This cannot be undone.")) {
            e.preventDefault();
          }
        }}
      >
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <input type="hidden" name="_method" value="delete" />
        <Button type="submit" variant="destructive" size="sm">
          Delete
        </Button>
      </form>
    </div>
  );

  return (
    <DashboardPageLayout
      title={post.title}
      subtitle={
        <span>
          Slug: <code>{post.slug}</code> ·{" "}
          <a
            href={publicHref}
            className="text-premium-600 hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            View public URL
          </a>
        </span>
      }
      headerActions={headerActions}
    >
      <div className="mb-4">
        <ButtonLink
          to={adminRoutes.adminBlogPosts}
          variant="ghost"
          size="sm"
          className="gap-1.5 px-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to all posts
        </ButtonLink>
      </div>

      {verifyMatches.length > 0 ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 mb-6">
          <p className="font-semibold">
            {verifyMatches.length} unverified statistic
            {verifyMatches.length === 1 ? "" : "s"} flagged
          </p>
          <p className="mt-1">
            Replace each <code>[VERIFY]</code> note with a sourced figure (or
            remove it) before publishing.
          </p>
          <ul className="mt-2 list-disc pl-5">
            {verifyMatches.slice(0, 5).map((line, idx) => (
              <li key={idx} className="font-mono text-xs">
                {line.trim()}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Edit content</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              method="post"
              action={updateAction}
              encType="multipart/form-data"
              onSubmit={() => setSubmitting(true)}
              className="space-y-4"
            >
              <input
                type="hidden"
                name="authenticity_token"
                value={csrfToken}
              />
              <input type="hidden" name="_method" value="patch" />
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="blog_post[title]"
                  defaultValue={post.title}
                  required
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  name="blog_post[slug]"
                  defaultValue={post.slug}
                  required
                />
              </div>
              <div>
                <Label htmlFor="meta_description">Meta description</Label>
                <Input
                  id="meta_description"
                  name="blog_post[meta_description]"
                  defaultValue={post.metaDescription ?? ""}
                  maxLength={320}
                />
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Hero image</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    JPG or PNG, 16:9 ratio recommended (1600×900). Leave empty
                    to keep the current image.
                  </p>
                </div>
                {post.heroImageUrl ? (
                  <div className="aspect-video overflow-hidden rounded-md bg-card max-w-md">
                    <img
                      src={post.heroImageUrl}
                      alt={post.heroImageAlt ?? post.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <p className="text-xs italic text-muted-foreground">
                    No hero image attached yet.
                  </p>
                )}
                <div>
                  <Input
                    id="hero_image"
                    name="blog_post[hero_image]"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_image_alt">Hero image alt text</Label>
                  <Input
                    id="hero_image_alt"
                    name="blog_post[hero_image_alt]"
                    defaultValue={post.heroImageAlt ?? ""}
                    placeholder="Describe the image for accessibility & SEO"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="excerpt_md">Excerpt (markdown)</Label>
                <Textarea
                  id="excerpt_md"
                  name="blog_post[excerpt_md]"
                  defaultValue={post.excerptMd ?? ""}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="body_md">Body (markdown)</Label>
                <Textarea
                  id="body_md"
                  name="blog_post[body_md]"
                  defaultValue={post.bodyMd}
                  rows={20}
                  required
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="author_name">Author</Label>
                <Input
                  id="author_name"
                  name="blog_post[author_name]"
                  defaultValue={post.authorName ?? "ToolKit Team"}
                />
              </div>
              <div>
                <Label htmlFor="target_keyword">Target keyword</Label>
                <Input
                  id="target_keyword"
                  name="blog_post[target_keyword]"
                  defaultValue={post.targetKeyword ?? ""}
                />
              </div>
              <div>
                <Label htmlFor="trade_tags">
                  Trade tags (comma separated slugs)
                </Label>
                <Input
                  id="trade_tags"
                  name="blog_post[trade_tags]"
                  defaultValue={tradeTagsString}
                  placeholder={tradeOptions
                    .slice(0, 3)
                    .map((t) => t.slug)
                    .join(",")}
                />
              </div>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Save changes"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {post.heroImageUrl ? (
              <div className="aspect-video overflow-hidden rounded-md mb-6 bg-muted">
                <img
                  src={post.heroImageUrl}
                  alt={post.heroImageAlt ?? post.title}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : null}
            <div
              className="markdown-content markdown-content-sm"
              dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
            />
            {post.generatedByModel ? (
              <p className="mt-6 text-xs text-muted-foreground">
                Generated by <code>{post.generatedByModel}</code>
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </DashboardPageLayout>
  );
};
